import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, History } from "lucide-react";
import { Label } from "@/components/ui/label";
import { GroupChip } from "@/components/common/Visuals";
import { KINDS } from "@/lib/kinds";
import { cn } from "@/lib/utils";

export function BackLink({ to, label }) {
  return (
    <Link to={to} data-testid="back-link" className="mb-5 mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-stone-500 hover:text-primary">
      <ArrowLeft className="h-4 w-4" /> {label}
    </Link>
  );
}

export function DetailCard({ title, icon: Icon, children, className, testId, action }) {
  return (
    <section className={cn("lm-card p-6", className)} data-testid={testId}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-base font-bold">{Icon && <Icon className="h-4 w-4 text-primary" />}{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export function LinkedGroupChips({ groups, empty = "—" }) {
  if (!groups?.length) return <span className="text-sm text-muted-foreground">{empty}</span>;
  return (
    <div className="flex flex-wrap gap-2">
      {groups.map((g) => (
        <Link key={g.id} to={`${KINDS[g.kind].path}/${g.id}`} className="transition-opacity hover:opacity-80" data-testid={`group-link-${g.id}`}>
          <GroupChip group={g} size="md" />
        </Link>
      ))}
    </div>
  );
}

export function HistorySection({ sections }) {
  const filled = sections.filter((s) => s.items?.length);
  if (!filled.length) return null;
  return (
    <section className="mt-6 rounded-2xl border border-dashed border-border bg-white/60 p-6" data-testid="history-section">
      <h2 className="flex items-center gap-2 text-base font-bold"><History className="h-4 w-4 text-[#EC7F13]" /> Historique La Mêlée</h2>
      <div className="mt-4 grid gap-6 md:grid-cols-3">
        {filled.map((s) => (
          <div key={s.label}>
            <div className="text-[11px] font-bold uppercase tracking-wider text-stone-500">{s.label} ({s.items.length})</div>
            <ul className="mt-2 space-y-1.5">
              {s.items.map((item, i) => (
                <li key={`${item.title}-${i}`} className="flex items-center gap-2 text-sm">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#EC7F13]" />
                  <span className="truncate">{item.title}</span>
                  {item.url && <a href={item.url} target="_blank" rel="noreferrer" className="text-stone-400 hover:text-primary" aria-label="Ouvrir dans Notion"><ExternalLink className="h-3 w-3" /></a>}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

export function Field({ label, required, error, hint, htmlFor, children, className }) {
  return (
    <div className={cn("space-y-1.5", className)}>
      <Label htmlFor={htmlFor} className="text-sm font-semibold">
        {label}{required && <span className="text-primary"> *</span>}
      </Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && <p className="text-xs font-semibold text-red-600" data-testid={`error-${htmlFor}`}>{error}</p>}
    </div>
  );
}

export function Suggestions({ id, values }) {
  return <datalist id={id}>{(values || []).map((v) => <option key={v} value={v} />)}</datalist>;
}

export function FormSection({ title, description, children }) {
  return (
    <section className="lm-card p-6 sm:p-7">
      <h2 className="text-lg font-bold">{title}</h2>
      {description && <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>}
      <div className="mt-5 grid gap-5 sm:grid-cols-2">{children}</div>
    </section>
  );
}
